// @flow
import type { FSA } from 'types/redux';
import type { CohortPassenger, Behaviour, Location } from 'types/entities';
import type { BookingRequest, Booking, BookingRequestV2 } from 'types/bookings';
import { toBookingRequestV2 } from 'utils/booking-utils';
import ga from 'react-ga';
import { Map } from 'immutable';

import { PASSENGERS, PASSENGERS_ACTIONS } from 'utils/ga-utils';
import { API_REQUEST } from 'middlewares/api-middleware';

export const PASSENGER_LOGIN: string = 'PASSENGER_LOGIN';
export function passengerLoginInstant(cohortID: string, passengerID: string,
  behaviour: Behaviour): FSA {
  return {
    type: PASSENGER_LOGIN,
    payload: {
      cohortID,
      passengerID,
      behaviour,
    },
  };
}

export function passengerLogin(cohortID: string, passengerID: string,
  passenger: CohortPassenger): FSA {
  ga.event({
    category: PASSENGERS,
    action: PASSENGERS_ACTIONS.PASSENGER_LOGIN,
  });
  return {
    type: PASSENGER_LOGIN,
    payload: {
      method: 'POST',
      url: `/passengers/${passengerID}/login`,
      data: {
        behaviour: passenger.behaviour,
      },
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
      passengerID,
    },
  };
}

export const PASSENGER_LOGOUT: string = 'PASSENGER_LOGOUT';
export function passengerLogoutInstant(cohortID: string, passengerID: string): FSA {
  return {
    type: PASSENGER_LOGOUT,
    payload: {
      cohortID,
      passengerID,
    },
  };
}

export function passengerLogout(cohortID: string, passengerID: string): FSA {
  ga.event({
    category: PASSENGERS,
    action: PASSENGERS_ACTIONS.PASSENGER_LOGOUT,
  });
  return {
    type: PASSENGER_LOGOUT,
    payload: {
      method: 'POST',
      url: `/passengers/${passengerID}/logout`,
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
      passengerID,
    },
  };
}

export const PASSENGER_BOOKING_CREATE: string = 'PASSENGER_BOOKING_CREATE';
export function passengerBookingCreate(cohortID: string, passengerID: string,
  booking: BookingRequest, pois: Map): FSA {
  ga.event({
    category: PASSENGERS,
    action: PASSENGERS_ACTIONS.PASSENGER_BOOKING_CREATE,
  });

  const bkgReq: BookingRequestV2 = toBookingRequestV2(booking, pois);
  return {
    type: PASSENGER_BOOKING_CREATE,
    payload: {
      method: 'POST',
      url: `/passengers/${passengerID}/bookings/multi-stop`,
      data: bkgReq,
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
      passengerID,
      booking: {
        ...bkgReq,
        passengerID,
      },
    },
  };
}

export const PASSENGER_BOOKING_GET: string = 'PASSENGER_BOOKING_GET';
export function passengerBookingGet(cohortID: string, passengerID: string,
  bookingID: string): FSA {
  ga.event({
    category: PASSENGERS,
    action: PASSENGERS_ACTIONS.PASSENGER_BOOKING_GET,
  });
  return {
    type: PASSENGER_BOOKING_GET,
    payload: {
      method: 'GET',
      url: `/passengers/${passengerID}/bookings/${bookingID}`,
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
      passengerID,
      bookingID,
    },
  };
}

export const PASSENGER_BOOKING_CANCEL: string = 'PASSENGER_BOOKING_CANCEL';
export function passengerBookingCancel(cohortID: string, passengerID: string,
  bookingID: string): FSA {
  ga.event({
    category: PASSENGERS,
    action: PASSENGERS_ACTIONS.PASSENGER_BOOKING_CANCEL,
  });
  return {
    type: PASSENGER_BOOKING_CANCEL,
    payload: {
      method: 'POST',
      url: `/passengers/${passengerID}/bookings/${bookingID}/cancel`,
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
      passengerID,
      bookingID,
    },
  };
}

export const PASSENGER_DELETE: string = 'PASSENGER_DELETE';
export function passengerDeleteInstant(cohortID: string, passengerID: string): FSA {
  return {
    type: PASSENGER_DELETE,
    payload: {
      cohortID,
      passengerID,
    },
  };
}

export function passengerDelete(cohortID: string, passengerID: string): FSA {
  ga.event({
    category: PASSENGERS,
    action: PASSENGERS_ACTIONS.PASSENGER_DELETE,
  });
  return {
    type: PASSENGER_DELETE,
    payload: {
      method: 'DELETE',
      url: `/passengers/${passengerID}`,
    },
    meta: {
      [API_REQUEST]: true,
      cohortID,
      passengerID,
    },
  };
}


export const PASSENGER_BOOKING_NEW: string = 'PASSENGER_BOOKING_NEW';
export function passengerBookingNew(cohortID: string, passengerID: string,
  bookingID: string, booking: Booking): FSA {
  ga.event({
    category: PASSENGERS,
    action: PASSENGERS_ACTIONS.PASSENGER_BOOKING_NEW,
  });
  return {
    type: PASSENGER_BOOKING_NEW,
    payload: {
      cohortID,
      passengerID,
      bookingID,
      booking,
    },
  };
}

export const PASSENGER_BOOKING_STATE_CHANGE = 'PASSENGER_BOOKING_STATE_CHANGE';
export function passengerBookingStateChange(cohortID: string, passengerID: string,
  bookingID: string, state: number): FSA {
  ga.event({
    category: PASSENGERS,
    action: PASSENGERS_ACTIONS.PASSENGER_BOOKING_STATE_CHANGE,
  });
  return {
    type: PASSENGER_BOOKING_STATE_CHANGE,
    payload: {
      cohortID,
      passengerID,
      bookingID,
      state,
    },
  };
}

export const PASSENGER_BOOKING_DELETE = 'PASSENGER_BOOKING_DELETE';
export function passengerBookingDelete(cohortID: string, passengerID: string,
  bookingID: string): FSA {
  ga.event({
    category: PASSENGERS,
    action: PASSENGERS_ACTIONS.PASSENGER_BOOKING_DELETE,
  });
  return {
    type: PASSENGER_BOOKING_DELETE,
    payload: {
      cohortID,
      passengerID,
      bookingID,
    },
  };
}

export const PASSENGER_GET_NEARBY_TAXI_TYPES = 'PASSENGER_GET_NEARBY_TAXI_TYPES';
export function passengerGetNearbyTaxiTypes(passengerID: string, pickUpPoint: Location): FSA {
  return {
    type: PASSENGER_GET_NEARBY_TAXI_TYPES,
    payload: {
      method: 'GET',
      url: `/passengers/${passengerID}/nearby-taxi-types`,
      params: {
        latitude: pickUpPoint[1],
        longitude: pickUpPoint[0],
      },
    },
    meta: {
      [API_REQUEST]: true,
      passengerID,
      pickUpPoint,
    },
  };
}

export const PASSENGER_CHANGE_DESTINATION = 'PASSENGER_CHANGE_DESTINATION';
export function passengerChangeDestination(cohortID: string, passengerID: string,
  bookingID: string, dest: Location): FSA {
  const dropoffPoint = {
    latitude: dest[0],
    longitude: dest[1],
    // TODO: to be removed once the backend is fixed
    address: 'address',
    keywords: 'keywords',
  };

  return {
    type: PASSENGER_CHANGE_DESTINATION,
    payload: {
      method: 'POST',
      url: `/passengers/${passengerID}/bookings/${bookingID}/change_destination`,
      data: {
        updatedDestination: dropoffPoint,
      },
    },
    meta: {
      [API_REQUEST]: true,
      dropoffPoint,
      passengerID,
      bookingID,
      cohortID,
    },
  };
}



/** WEBPACK FOOTER **
 ** ./src/js/actions/passengers.js
 **/